import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

import { Events } from 'src/providers/events/events';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Loading } from 'src/shared/components/loading/loading';
import { Util } from 'src/shared/util/util';
import validationUtils from 'src/shared/util/validation-utils';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { Vendors } from 'src/providers/vendors/vendors';
import {
  CUSTOMER_VENDORS_CHANGED_EVENT,
  Vendor,
  VendorCategory,
  VendorCreatePayload,
  VendorUpdatePayload,
} from 'src/models/vendor.model';

type EditVendorMode = 'create' | 'edit';

interface VendorFormShape {
  vendor_category_id: FormControl<number | null>;
  name: FormControl<string>;
  company_name: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  address1: FormControl<string>;
  address2: FormControl<string>;
  city: FormControl<string>;
  state: FormControl<string>;
  zipcode: FormControl<string>;
  country_code: FormControl<string>;
  description: FormControl<string>;
  notes: FormControl<string>;
}

const NON_BLANK = (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value ?? '').toString().trim();
  return value.length > 0 ? null : { required: true };
};

@Component({
  templateUrl: 'edit-vendor.html',
  standalone: false,
})
export class EditVendorPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly events = inject(Events);
  private readonly modalCtrl = inject(ModalController);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly vendors = inject(Vendors);
  private readonly util = inject(Util);
  private readonly route = inject(ActivatedRoute);

  mode: EditVendorMode = 'create';
  vendor: Vendor | null = null;
  vendorId: number | null = null;
  form!: FormGroup<VendorFormShape>;
  categories: VendorCategory[] = [];
  categoryItems: { value: number; viewValue: string }[] = [];
  submitted = false;
  loaded = false;
  isRightSideContent = true;
  errorMessage = '';
  dialogParams: any;

  boundSave = this.save.bind(this);
  boundDelete = this.deleteVendor.bind(this);

  constructor() {
    this.form = this.fb.group<VendorFormShape>({
      vendor_category_id: this.fb.control<number | null>(null, Validators.required),
      name: this.fb.nonNullable.control('', [NON_BLANK, Validators.maxLength(100)]),
      company_name: this.fb.nonNullable.control(''),
      email: this.fb.nonNullable.control('', [validationUtils.validateEmail]),
      phone: this.fb.nonNullable.control(''),
      address1: this.fb.nonNullable.control(''),
      address2: this.fb.nonNullable.control(''),
      city: this.fb.nonNullable.control(''),
      state: this.fb.nonNullable.control(''),
      zipcode: this.fb.nonNullable.control(''),
      country_code: this.fb.nonNullable.control('US'),
      description: this.fb.nonNullable.control(''),
      notes: this.fb.nonNullable.control(''),
    });
  }

  @Loading('', true)
  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    this.mode = ((await this.getParam('mode')) as EditVendorMode) || 'create';
    const passedVendor = (await this.getParam('vendor')) as Vendor | null;
    const routeId = Number(this.route.snapshot.paramMap.get('vendorId')) || null;
    this.vendorId = passedVendor?.id ?? routeId ?? (await this.getParam('vendorId')) ?? null;

    if (this.mode === 'edit' && !passedVendor && this.vendorId) {
      try {
        this.vendor = await firstValueFrom(this.vendors.getVendor(this.vendorId));
      } catch (err: any) {
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load vendor';
      }
    } else {
      this.vendor = passedVendor;
    }

    try {
      this.categories = await firstValueFrom(this.vendors.getVendorCategories());
      this.categoryItems = this.categories.map(c => ({ value: c.id, viewValue: c.name }));
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load categories';
    }

    this.rightSidePanelService.setDialogPageTitle(this.mode === 'edit' ? 'Edit Vendor' : 'Add Vendor');

    if (this.mode === 'edit' && this.vendor) {
      this.fillForm(this.vendor);
    }
    this.loaded = true;
  }

  private fillForm(vendor: Vendor) {
    this.form.patchValue({
      vendor_category_id: vendor.vendor_category?.id ?? null,
      name: vendor.name ?? '',
      company_name: vendor.company_name ?? '',
      email: vendor.email ?? '',
      phone: vendor.phone ?? '',
      address1: vendor.address1 ?? '',
      address2: vendor.address2 ?? '',
      city: vendor.city ?? '',
      state: vendor.state ?? '',
      zipcode: vendor.zipcode ?? '',
      country_code: vendor.country_code ?? 'US',
      description: vendor.description ?? '',
      notes: vendor.notes ?? '',
    });
  }

  private async getParam(name: string): Promise<any> {
    let param = this.dialogParams?.[name];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(name);
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(name);
    }
    this.storage.save(name, param);
    return param;
  }

  @Loading('', true)
  async save() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.form.valid) {
      return;
    }

    try {
      if (this.mode === 'create') {
        const value = this.form.getRawValue();
        const payload: VendorCreatePayload = {
          vendor_category_id: value.vendor_category_id!,
          name: value.name.trim(),
          ...this.optional('company_name', value.company_name),
          ...this.optional('email', value.email),
          ...this.optional('phone', value.phone),
          ...this.optional('address1', value.address1),
          ...this.optional('address2', value.address2),
          ...this.optional('city', value.city),
          ...this.optional('state', value.state),
          ...this.optional('zipcode', value.zipcode),
          ...this.optional('country_code', value.country_code),
          ...this.optional('description', value.description),
          ...this.optional('notes', value.notes),
        };
        await firstValueFrom(this.vendors.createVendor(payload));
        this.util.showSuccess('Vendor added');
      } else if (this.vendor) {
        const payload = this.buildUpdatePayload();
        if (Object.keys(payload).length === 0) {
          this.dismiss();
          return;
        }
        await firstValueFrom(this.vendors.updateVendor(this.vendor.id, payload));
        this.util.showSuccess('Vendor updated');
      }
      this.events.publish(CUSTOMER_VENDORS_CHANGED_EVENT);
      this.dismiss();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Could not save vendor';
    }
  }

  async deleteVendor() {
    if (!this.vendor) { return; }
    const params: ConfirmPageParamsModel = {
      title: 'Remove Vendor',
      body: this.vendor.name
        ? `Are you sure you want to remove ${this.vendor.name}?`
        : 'Are you sure you want to remove this vendor?',
      backText: 'Go Back',
      confirmText: 'Remove Vendor',
      confirmAction: this.confirmDeleteVendor.bind(this),
      confirmButtonClass: 'secondary',
    };
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await modal.present();
  }

  private async confirmDeleteVendor() {
    if (!this.vendor) { return; }
    try {
      await firstValueFrom(this.vendors.deleteVendor(this.vendor.id));
      this.events.publish(CUSTOMER_VENDORS_CHANGED_EVENT);
      this.util.showSuccess('Vendor removed');
      this.dismiss();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Could not remove vendor';
      throw err;
    } finally {
      await this.modalCtrl.dismiss();
    }
  }

  private buildUpdatePayload(): VendorUpdatePayload {
    const payload: VendorUpdatePayload = {};
    const v = this.form.getRawValue();
    const compare: Array<[keyof VendorUpdatePayload, string | number | null]> = [
      ['vendor_category_id', v.vendor_category_id],
      ['name', v.name.trim()],
      ['company_name', v.company_name.trim()],
      ['email', v.email.trim()],
      ['phone', v.phone.trim()],
      ['address1', v.address1.trim()],
      ['address2', v.address2.trim()],
      ['city', v.city.trim()],
      ['state', v.state.trim()],
      ['zipcode', v.zipcode.trim()],
      ['country_code', v.country_code.trim()],
      ['description', v.description.trim()],
      ['notes', v.notes.trim()],
    ];
    const current: any = {
      vendor_category_id: this.vendor?.vendor_category?.id ?? null,
      name: this.vendor?.name ?? '',
      company_name: this.vendor?.company_name ?? '',
      email: this.vendor?.email ?? '',
      phone: this.vendor?.phone ?? '',
      address1: this.vendor?.address1 ?? '',
      address2: this.vendor?.address2 ?? '',
      city: this.vendor?.city ?? '',
      state: this.vendor?.state ?? '',
      zipcode: this.vendor?.zipcode ?? '',
      country_code: this.vendor?.country_code ?? '',
      description: this.vendor?.description ?? '',
      notes: this.vendor?.notes ?? '',
    };
    for (const [key, value] of compare) {
      if (value !== current[key]) {
        (payload as any)[key] = value;
      }
    }
    return payload;
  }

  private optional<T>(key: string, value: T): Record<string, T> | {} {
    return value ? { [key]: value } : {};
  }

  private dismiss() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
